// Файл tag_utils.js

/**
 * Модуль содержит чистые функции, на которых построен режим текстовых тегов
 * компонента Input; он не зависит от React, состояния интерфейса или DOM.
 * Перед анализом определения тегов нормализуются: допустимы как простые
 * строки, так и объекты с value, name, label или description, а фигурные
 * скобки вокруг имени при необходимости удаляются. tokenize_tag_text
 * последовательно просматривает строку, находит пары фигурных скобок и
 * формирует фрагменты text, valid или invalid, не изменяя исходный текст.
 * Тег считается корректным только тогда, когда его имя соответствует
 * разрешённому формату и присутствует в переданном конкретному Input списке
 * допустимых тегов; неизвестные и незакрытые конструкции помечаются как
 * ошибочные. get_invalid_tags собирает такие фрагменты без повторов, чтобы
 * форма могла отдельно решить, показывать предупреждение или блокировать
 * дальнейшее действие. get_tag_suggestion_state анализирует часть строки до
 * позиции курсора, ищет последнюю незакрытую открывающую скобку и фильтрует
 * варианты по уже введённому префиксу, поэтому подсказка появляется именно
 * в месте текущего ввода. Возвращаемые start и end задают диапазон, который
 * TaggedInput заменяет выбранным полным тегом, не разбирая строку повторно.
 * Благодаря отсутствию побочных эффектов все правила разбора, проверки и
 * подбора подсказок можно тестировать независимо от визуального компонента.
 */

const TAG_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;


const normalize_tag_name = (value) => (
    String(value ?? '')
        .trim()
        .replace(/^\{/, '')
        .replace(/\}$/, '')
);


const normalize_tag_options = (tags = []) => (
    (Array.isArray(tags) ? tags : [])
        .map((item) => {
            if (typeof item === 'string') {
                const value = normalize_tag_name(item);

                return {
                    value,
                    label: '',
                };
            }

            const value = normalize_tag_name(
                item?.value ?? item?.name,
            );

            return {
                value,
                label: String(
                    item?.label
                    ?? item?.description
                    ?? '',
                ).trim(),
            };
        })
        .filter((item) => (
            TAG_NAME_PATTERN.test(item.value)
        ))
);


const tag_name_set = (tags = []) => new Set(
    normalize_tag_options(tags).map((item) => item.value),
);


const tokenize_tag_text = (value, tags = []) => {
    const text = String(value ?? '');
    const allowed = tag_name_set(tags);
    const parts = [];
    let index = 0;

    while (index < text.length) {
        const open_index = text.indexOf('{', index);

        if (open_index < 0) {
            parts.push({
                kind: 'text',
                text: text.slice(index),
            });
            break;
        }

        if (open_index > index) {
            parts.push({
                kind: 'text',
                text: text.slice(index, open_index),
            });
        }

        const close_index = text.indexOf(
            '}',
            open_index + 1,
        );

        if (close_index < 0) {
            parts.push({
                kind: 'invalid',
                text: text.slice(open_index),
            });
            break;
        }

        const token_text = text.slice(
            open_index,
            close_index + 1,
        );
        const token_name = text.slice(
            open_index + 1,
            close_index,
        );
        const is_valid = (
            TAG_NAME_PATTERN.test(token_name)
            && allowed.has(token_name)
        );

        parts.push({
            kind: is_valid ? 'valid' : 'invalid',
            text: token_text,
        });
        index = close_index + 1;
    }

    if (!parts.length) {
        parts.push({
            kind: 'text',
            text,
        });
    }

    return parts;
};


const get_invalid_tags = (value, tags = []) => {
    const invalid = tokenize_tag_text(
        value,
        tags,
    )
        .filter((part) => part.kind === 'invalid')
        .map((part) => part.text);

    return [...new Set(invalid)];
};


const get_tag_suggestion_state = (
    value,
    caret_position,
    tags = [],
) => {
    const text = String(value ?? '');
    const caret = Math.max(
        0,
        Math.min(
            Number(caret_position) || 0,
            text.length,
        ),
    );
    const prefix = text.slice(0, caret);
    const open_index = prefix.lastIndexOf('{');
    const close_index = prefix.lastIndexOf('}');

    if (open_index < 0 || open_index < close_index) {
        return null;
    }

    const query = prefix.slice(open_index + 1);

    if (!/^[A-Za-z0-9_]*$/.test(query)) {
        return null;
    }

    const normalized_query = query.toLowerCase();
    const options = normalize_tag_options(tags)
        .filter((item) => (
            item.value
                .toLowerCase()
                .startsWith(normalized_query)
        ));

    if (!options.length) {
        return null;
    }

    return {
        start: open_index,
        end: caret,
        options,
    };
};


export {
    get_invalid_tags,
    get_tag_suggestion_state,
    normalize_tag_options,
    tokenize_tag_text,
};
